import {
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { IonInput } from '@ionic/angular';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/auth/authentication/authentication.service';
import { SpecialCharacters, UserRoles } from 'src/app/core/enums';
import { Screens } from 'src/app/core/enums/role-permissions.enum';
import {
  CurrentLocation,
  GetLocationProfile,
  LinkedSite,
  LocationGroup,
  UserAdmin,
} from 'src/app/core/models/admin';
import { PackageInquiry } from 'src/app/core/models/package-tracking.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { RolePermissionsUtils } from 'src/app/core/utils/role-permissions/role-permissions.utils';
import { BANNER_TYPE } from '../banner/banner.constants';
import { PackageTrackingPopupComponent } from '../package/package-tracking-popup/package-tracking-popup.component';
import { ProfileModalComponent } from '../shared/modal/profile-modal/profile-modal.component';
import { FORM_STATUS } from '../shared/modal/profile-modal/profile-modal.constants';
import { IMAGE_SRC } from '../shared/shared-constants';
import { DISPLAY_CONSTANTS } from './top-nav.constants';

@Component({
  selector: 'app-top-nav',
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.scss'],
  standalone: false,
})
export class TopNavComponent implements OnInit {
  @ViewChild('groupSearchInput', { static: false }) groupSearchInput!: IonInput;
  @ViewChild('siteSearchInput', { static: false }) siteSearchInput!: IonInput;

  commonUtils = inject(CommonUtils);
  permissions = inject(RolePermissionsUtils);
  authService = inject(AuthenticationService);
  userService = inject(UserService);
  locationService = inject(LocationService);
  private dataService = inject(DataService);
  private dialog = inject(MatDialog);
  private roleUtils = inject(RolePermissionsUtils);
  private destroy = inject(DestroyRef);

  userDisplayName = signal(DISPLAY_CONSTANTS.LOADING_TEXT);

  searchQuery = '';
  groupSearch: string = '';
  siteSearch: string = '';
  currentLocation!: CurrentLocation;
  currentUser!: UserAdmin;
  userRole!: string;
  locationGroups: LocationGroup[] = [];
  linkedSites: LinkedSite[] = [];
  pageHeader = signal<string>('');

  readonly IMAGES = IMAGE_SRC;
  readonly DISPLAY = DISPLAY_CONSTANTS;
  readonly SCREENS = Screens;

  ngOnInit(): void {
    //When current location changes update user profile
    this.currentLocationChange();

    this.dataService.pageHeader$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((header) => {
        this.pageHeader.set(header);
      });

    // linked sites and location depend on user state
    // these props need to be updated on user change
    this.userService
      .getCurrentUser$()
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroy),
        catchError(() => {
          return of(undefined);
        }),
        filter((val) => !!val),
        switchMap((emitUser) => {
          this.setUserInfo(emitUser);
          if (this.locationGroups.length > 0) {
            return of();
          } else {
            return this.getLocationGroups();
          }
        }),
      )
      .subscribe();
  }

  toggleDrawer(): void {
    if (this.authService.allowDrawerToggle()) {
      this.authService.isDrawerOpen$
        .asObservable()
        .pipe(
          take(1),
          tap((val) => {
            this.authService.isDrawerOpen$.next(!val);
          }),
        )
        .subscribe();
    }
  }

  showSiteControls(code: string): boolean {
    const validCode = (code && code.length > 0) || false;
    const permission = this.currentUser?.roleName === UserRoles.ADMIN || false;
    return validCode && permission;
  }

  openPackageTrackingPopup(data: PackageInquiry | null): void {
    this.dialog
      .open(PackageTrackingPopupComponent, {
        width: '100vw',
        maxWidth: '100vw',
        panelClass: 'full-screen-dialog',
        height: '95vh',
        disableClose: true,
        data: {
          searchQuery: this.searchQuery,
          originDetails: data?.originDetails || {},
          destinationDetails: data?.destDetails || {},
          accessorials: data?.accessorials || {},
          hazmatAccessorials: data?.hazmatAccessorials || {},
          latestTracknum: data?.latestTracknum || [],
        },
      })
      .afterOpened()
      .pipe(take(1))
      .subscribe(() => {
        this.commonUtils.updateLoadingModal(false);
        this.searchQuery = '';
      });
  }

  gotoProfile(): void {
    this.dialog
      .open(ProfileModalComponent, {
        width: '47.5rem',
        disableClose: true,
        data: {
          data: { ...this.currentUser },
          formStatus: FORM_STATUS.VIEW_EDIT,
          isOwnProfile: true,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((model) => {
        if (model) {
          this.userService
            .editUser(model.data)
            .pipe(
              catchError(() => {
                return of(null);
              }),
              filter((val) => !!val),
            )
            .subscribe(() => {
              this.userService.setCurrentUser$(model.data);
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            });
        }
      });
  }

  handleGroupChange(code: string): void {
    this.commonUtils.updateBanner(false);
    if (code !== '' && code !== this.currentLocation.groupCode) {
      this.locationService
        .getLinkedSitesByGroup(code)
        .pipe(
          take(1),
          switchMap((data) => {
            this.linkedSites = data?.response?.locations || [];
            this.linkedSites.sort((a, b) => a.code.localeCompare(b.code));
            if (this.linkedSites.length > 0) {
              return this.locationService
                .getLocationProfile(this.linkedSites[0].code)
                .pipe(takeUntilDestroyed(this.destroy));
            } else {
              this.commonUtils.updateBannerType(BANNER_TYPE.NO_LOCATIONS);
              return of(false);
            }
          }),
          filter((value) => !!value),
        )
        .subscribe((returnValue) => {
          this.locationService.setCurrentLocation$({
            groupCode: code,
            location: (returnValue as GetLocationProfile).response,
          });
        });
    }
  }

  handleLocationChange(code: string): void {
    if (code !== '' && code !== this.currentUser.location) {
      this.getCurrentLocation(this.currentLocation.groupCode, code);
    }
  }

  pressKey($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      // need to update event handling here to get text from the search field
      if (this.searchQuery.length === 0) {
        return;
      }

      //To save api call time only call package details here.  When switching to other tab will call history
      this.dataService
        .getPackageDetailsById(
          this.searchQuery.toUpperCase(),
          this.currentUser.location,
        )
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe((res) => {
          this.openPackageTrackingPopup(res?.response?.packageInquiry[0]);
        });
    } else {
      this.commonUtils.preventSpecialCharacters(
        $event,
        SpecialCharacters.ALPHABET_NUMBER_NO_SPACE,
      );
    }
  }

  resetValues() {
    this.groupSearch = '';
    this.siteSearch = '';
  }

  resetGroupValue() {
    this.groupSearch = '';
    this.onGroupMenuOpened();
  }

  onGroupMenuOpened(): void {
    setTimeout(() => {
      this.groupSearchInput?.setFocus();
    }, 0);
  }

  resetSiteValue() {
    this.siteSearch = '';
    this.onSiteMenuOpened();
  }

  onSiteMenuOpened(): void {
    setTimeout(() => {
      this.siteSearchInput?.setFocus();
    }, 0);
  }

  get canEditProfile() {
    const role = this.currentUser?.roleName ?? '';
    return this.roleUtils.canSeeUserProfile(role);
  }

  get canSeeLocations() {
    const role = this.currentUser?.roleName;
    return this.roleUtils.canChangeOwnLocation(role);
  }

  get canSeeGroups() {
    const role = this.currentUser?.roleName;
    return this.roleUtils.canChangeOwnGroup(role);
  }

  private toggleLoading(flag: boolean): void {
    this.commonUtils.updateLoadingModal(flag);
    this.commonUtils.lockUpdateLoadingModal(flag);
  }

  private sortLinkedSite(
    array: LinkedSite[] | LocationGroup[],
  ): LinkedSite[] | LocationGroup[] {
    return array?.sort((a, b) => a.code.localeCompare(b.code)) || [];
  }

  private getGroupLinkedSites() {
    this.toggleLoading(true);
    this.locationService
      .getLinkedSitesByGroup(
        this.currentUser.locationGroup,
        true,
        BANNER_TYPE.CURRENT_USER_LINKED_SITES_ERROR,
      )
      .pipe(
        take(1),
        catchError(() => {
          this.toggleLoading(false);
          return of(null);
        }),
        filter((val) => !!val),
      )
      .subscribe((getSites) => {
        if (getSites.response?.locations?.length > 0) {
          this.linkedSites = this.sortLinkedSite(
            getSites.response?.locations,
          ) as LinkedSite[];
        }
        if (
          this.currentLocation?.location?.locationCode !==
          this.currentUser.location
        ) {
          this.getCurrentLocation(
            this.currentUser?.locationGroup,
            this.currentUser?.location,
          );
        } else {
          this.toggleLoading(false);
        }
      });
  }

  private updateUserLocation(user: UserAdmin, emitLocation: CurrentLocation) {
    this.userService
      .editUser(user)
      .pipe(
        take(1),
        catchError(() => {
          this.toggleLoading(false);
          return of(null);
        }),
        filter((val) => !!val),
      )
      .subscribe(() => {
        // work with back-end to return copy of updated user here
        // instead of recycling front-end copy of changes without knowing
        // that db was successful.
        this.currentUser = user;
        this.userService.setCurrentUser$(user);
        this.currentLocation = emitLocation;
      });
  }

  private getCurrentLocation(groupCode: string, locationCode: string) {
    this.locationService
      .getLocationProfile(locationCode)
      .pipe(
        take(1),
        catchError(() => {
          this.toggleLoading(false);
          return of(null);
        }),
        filter((val) => !!val),
      )
      .subscribe((returnValue) => {
        this.locationService.setCurrentLocation$({
          groupCode: groupCode,
          location: returnValue.response,
        });
      });
  }

  private currentLocationChange() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((emitLocation) => {
        if (emitLocation) {
          if (
            emitLocation?.groupCode &&
            emitLocation?.location?.locationCode &&
            (emitLocation?.groupCode !== this.currentUser.locationGroup ||
              emitLocation?.location.locationCode !== this.currentUser.location)
          ) {
            this.toggleLoading(true);
            const user = { ...this.currentUser };
            user.location = emitLocation.location?.locationCode || '';
            user.locationGroup = emitLocation.groupCode || '';
            this.updateUserLocation(user, emitLocation);
          } else if (
            emitLocation.groupCode !== null &&
            emitLocation.location !== null
          ) {
            this.currentLocation = emitLocation;
            this.toggleLoading(false);
          }
        }
      });
  }

  private setUserInfo(emitUser: UserAdmin) {
    this.currentUser = emitUser;
    this.userDisplayName.set(
      this.currentUser.familyName.concat(', ', this.currentUser.givenName),
    );

    if (
      this.currentUser?.locationGroup &&
      this.currentUser?.location &&
      (this.currentLocation?.location?.locationCode !==
        this.currentUser.location ||
        this.currentLocation?.groupCode !== this.currentUser.locationGroup)
    ) {
      this.getGroupLinkedSites();
    } else {
      this.locationService.setCurrentLocation$(this.currentLocation);
    }
  }

  private getLocationGroups() {
    // this call only needs to be made once for the lifetime of the app
    // the location groups will not change
    return this.locationService
      .getLocationGroups(BANNER_TYPE.CURRENT_USER_LOCATION_GROUP_ERROR)
      .pipe(
        take(1),
        catchError(() => {
          this.toggleLoading(false);
          return of(null);
        }),
        filter((val) => !!val),
        tap((getGroups) => {
          this.locationGroups = this.sortLinkedSite(
            getGroups?.response?.locationGroups,
          ) as LocationGroup[];
        }),
        finalize(() => {
          this.toggleLoading(false);
        }),
      );
  }
}
